import { useCallback, useMemo, useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  formatE1rm,
  MIN_TREND_POINTS,
  MIN_TREND_SPAN_DAYS,
  type MovementPattern,
} from "@heraion/shared";

import { machineLabel } from "../Logger/machineLabel";
import { patternLabel } from "../patternLabel";
import { PanelTitleRow } from "./InfoPill";
import { ErrorCard, LoadingCard } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { isEmptyReportPeriod, panelDays } from "./progressWindow";
import { OCCUPANT_FRESHNESS_DAYS } from "./e1rmOccupancy";
import { TierOneTrendRow } from "./TierOneTrendRow";
import type { E1RMTrendEntry } from "./e1rmTrendMath";
import {
  panelE1RMRanking,
  sliceRows,
  trendCellPatterns,
  type RowSlice,
} from "./reportSectionRows";
import type { ProgressDeps } from "./types";
import { useExerciseNameResolver } from "./useExerciseNameResolver";
import { useSessionResultsFromDeps } from "./useSessionResults";
import { weightUnitLabels } from "../weightUnitLabels";

/**
 * One pattern's slot in the panel. An absent `row` is PG-24's empty cell: the
 * pattern WAS trained in the window and nothing cleared the bar. A pattern with
 * nothing logged never becomes a cell at all, so the panel keeps not drawing it.
 */
type TrendCell = {
  pattern: MovementPattern;
  row?: E1RMTrendEntry & { name: string };
};

// Top lift per movement pattern + sparkline + regression flag. The row itself
// (the three measurement-contract §3 states) is the shared TierOneTrendRow,
// which the reps-at-load axis renders too; this panel owns the e1RM data,
// the pattern grouping and its own i18n/units. `slice`: one report page's cells.
export function E1RMTrendsPanel({ slice, ...deps }: ProgressDeps & { slice?: RowSlice }) {
  const { theme, units } = deps;
  const { range } = deps;
  const windowDays = panelDays(range);
  const { t, i18n } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const { results, catalog, error } = useSessionResultsFromDeps(deps);
  const getName = useExerciseNameResolver(catalog);
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }),
    [i18n.language],
  );

  const ranking = useMemo(
    () => (results && catalog ? panelE1RMRanking({ results, catalog, range }) : null),
    [results, catalog, range],
  );

  // PG-24 — which exercise each pattern is compared with, when the athlete has
  // said so. ⛔ Panel-local on purpose, against the scout's suggestion to put it
  // beside the window in ProgressView: the window is a parameter every panel
  // reads, this is one panel's internal choice, and hoisting it would make
  // ProgressView carry a concept only the e1RM axis has. It does NOT survive
  // leaving the screen — persisting the choice is the next slice, where the
  // occupant handover is persisted too.
  const [chosen, setChosen] = useState<ReadonlyMap<MovementPattern, string>>(
    () => new Map(),
  );
  const [pickingFor, setPickingFor] = useState<MovementPattern | null>(null);
  const ExercisePickerMount = deps.ExercisePickerMount;
  const closePicker = useCallback(() => setPickingFor(null), []);
  const comparableSlugs = useMemo(
    () => [...(ranking?.comparable.keys() ?? [])],
    [ranking],
  );
  const pickCompared = useCallback(
    (ex: { slug: string }) => {
      setChosen((prev) => {
        if (pickingFor === null) return prev;
        const next = new Map(prev);
        next.set(pickingFor, ex.slug);
        return next;
      });
      setPickingFor(null);
    },
    [pickingFor],
  );
  // PG-28 — the way back. `chosen` had no delete: once a pattern held a pick,
  // the computed handover was unreachable for the rest of the session (PG-29).
  // ⛔ Nothing to compute here: `compareFor` below already falls back to
  // `r.previous` whenever the map has no entry, which is how every untouched
  // row renders today. Dropping the entry IS the default.
  const clearCompared = useCallback((pattern: MovementPattern) => {
    setChosen((prev) => {
      if (!prev.has(pattern)) return prev;
      const next = new Map(prev);
      next.delete(pattern);
      return next;
    });
  }, []);

  /** The curve drawn behind this row: what he picked, else the handover. */
  const compareFor = useCallback(
    (r: E1RMTrendEntry) => {
      const picked = chosen.get(r.pattern);
      const series = picked ? ranking?.comparable.get(picked) : r.previous;
      if (series === undefined) return undefined;
      return [
        {
          key: series.slug,
          points: series.sorted.map((p) => ({
            x: p.atMs,
            y: p.kgPerRep1,
            label: dateFmt.format(new Date(p.atMs)),
          })),
          // PG-26 (2026-09-17) — the NAME only; the row composes the `vs` and
          // paints it in the curve's colour.
          //
          // ⚠️ This used to be two different sentences, and dropping one was
          // his call, made against his own rule of the day before: "Previously"
          // was a FACT the app worked out, "vs" a curve he asked for by name,
          // and saying "Previously" about his own pick would put words in his
          // mouth. He replaced both with one separator and made the ✕ the
          // visible marker of his own choice instead.
          // ⛔ Which leaves nothing for a reader who cannot SEE the ✕ — so the
          // two sentences did not die, they moved into `a11yLabel` below.
          name: getName(series.slug, series.snapshot),
          separator: t("mobile.progress.trends.compareSeparator", {
            defaultValue: "vs",
          }),
          a11yLabel: picked
            ? t("mobile.progress.trends.comparedManualA11y", {
                current: getName(r.slug, r.snapshot),
                name: getName(series.slug, series.snapshot),
                defaultValue: "{{current}} compared with {{name}}, your choice",
              })
            : t("mobile.progress.trends.comparedWithA11y", {
                current: getName(r.slug, r.snapshot),
                name: getName(series.slug, series.snapshot),
                defaultValue:
                  "{{current}} compared with {{name}}, which held this pattern before",
              }),
        },
      ];
    },
    [chosen, ranking, dateFmt, t, getName],
  );

  // One cell per pattern the athlete has something to be told about
  // (trendCellPatterns): an occupant, or (PG-24) the measure that kept every
  // candidate out.
  const cells = useMemo(() => {
    if (!ranking) return null;
    const occupied = new Map(ranking.rows.map((r) => [r.pattern, r]));
    return sliceRows(trendCellPatterns(ranking), slice).map<TrendCell>((pattern) => {
      const row = occupied.get(pattern);
      // Name resolved here via the live catalog (FK = slug); the aggregation
      // carries only the deleted-row snapshot.
      return row
        ? { pattern, row: { ...row, name: getName(row.slug, row.snapshot) } }
        : { pattern };
    });
  }, [ranking, getName, slice]);

  const trendsTitle = t("mobile.progress.trends.title", {
    defaultValue: "E1RM trends",
  });
  // ⚠️ The legend must state the SAME rule the ranking applies. Before PG-24
  // it taught a "few data" badge below 3 points; a series that short can no
  // longer hold a pattern at all, so leaving that sentence would have the
  // panel explain a state it can never reach.
  const trendsHelp = t("mobile.progress.trends.help", {
    days: windowDays,
    points: MIN_TREND_POINTS,
    spanDays: MIN_TREND_SPAN_DAYS,
    freshDays: OCCUPANT_FRESHNESS_DAYS,
    defaultValue:
      "For each movement pattern (squat, hinge, push, pull, …) shows the exercise with the highest top e1RM, counting only exercises with a trend worth reading: at least {{points}} points spread over at least {{spanDays}} days. Chart = e1RM points over {{days}} days (tap a point to read its value and date). Arrow = trend over those days: ↗ progressing, → plateau, ↘ regressing. A trained pattern where nothing reaches that bar says so instead of showing a number. An exercise untrained for over {{freshDays}} days hands its pattern to the one trained now, and stays on the chart behind it.",
  });
  // PG-34: one always-visible line under the title, not the InfoPill body.
  const tapHint = t("progress.chartTapHint", {
    defaultValue: "Tap a point to read its value",
  });
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });
  // ⚠️ `spanDays`, not `days`: on this panel `{{days}}` already means the
  // panel's WINDOW (84) in `help` and `period`. Reusing that name here for
  // the fixed 14-day floor would put two different quantities behind one
  // placeholder in sibling keys — the next edit-by-analogy gets it wrong.
  const noEligibleText = t("mobile.progress.trends.noEligible", {
    points: MIN_TREND_POINTS,
    spanDays: MIN_TREND_SPAN_DAYS,
    defaultValue:
      "No exercise with at least {{points}} points over {{spanDays}} days.",
  });

  if (error) return <ErrorCard theme={theme} titleKey="trends" />;
  if (cells === null) return <LoadingCard theme={theme} titleKey="trends" />;

  if (cells.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={trendsTitle}
          body={trendsHelp}
          period={period}
        />
        <Text style={styles.empty}>
          {/* PG-64: a report reads its own period, which may be the past. Only
              a period without sessions says so; sessions with nothing to
              draw keep Progress's sentence. */}
          {isEmptyReportPeriod(deps, results)
            ? t("mobile.progress.report.emptyPeriod", {
                defaultValue: "No training in this period.",
              })
            : t("mobile.progress.empty", {
                defaultValue: "No recent activity to compare",
              })}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={trendsTitle}
        body={trendsHelp}
        period={period}
        caption={tapHint}
      />
      {cells.map(({ pattern, row: r }) =>
        r === undefined ? (
          // PG-24 — the cell keeps its place and STATES the measure that
          // emptied it. Not the shared TierOneTrendRow: that row owns how a
          // reading looks, and there is no reading here — no name, no value,
          // no series. Feeding it blanks would make a measurement row that
          // measures nothing.
          //
          // ⛔ `notMeasurable`, NOT `styles.empty`. `styles.empty` is the
          // PANEL-level voice — every sibling Tier-1 panel uses it to replace
          // a whole body that has nothing. This is a row among populated rows,
          // and the sentence is body copy the athlete must read: that is the
          // genre `panelStyles.notMeasurable` was written for, with the a11y
          // floor spelled out on it ("never the 11px italic badge, this is
          // instructional text for a reduced-vision reader"). Borrowing the
          // panel voice here would have shipped the exact thing that rule
          // forbids.
          //
          // Grouped for the screen reader, which the occupied rows are NOT:
          // they hold a machine chip that must stay its own stop, this cell
          // holds nothing tappable — so grouping costs nothing and turns two
          // disconnected stops into one sentence.
          <View
            key={pattern}
            style={styles.trendRow}
            accessible
            accessibilityLabel={`${patternLabel(t, pattern)} — ${noEligibleText}`}
          >
            <Text style={styles.patternLabel}>{patternLabel(t, pattern)}</Text>
            <Text style={styles.notMeasurable}>{noEligibleText}</Text>
          </View>
        ) : (
        <TierOneTrendRow
          key={r.pattern}
          theme={theme}
          leadLabel={patternLabel(t, r.pattern)}
          name={r.name}
          // §3 insufficient: points exist but none went near the limit, so an
          // arrow would be a confident line through sets we know nothing about.
          insufficient={r.trustedCount === 0}
          reg={r.reg}
          // PG-49 (CN-26 callsite that escaped the 11/09 migration) — an e1RM
          // is an ESTIMATE (Epley, ~5% inherent error), never a scale/plate
          // reading: `formatE1rm` (1 dp), not `formatWeight` (2 dp for kg).
          // `Intl` trims trailing zeros, so `188.3 kg` elsewhere on THIS same
          // screen looked like a different, coarser format — it wasn't; it
          // was the same `formatWeight` call landing on a value whose 2nd/3rd
          // decimal happened to be zero.
          value={formatE1rm(r.latest, units, i18n.language, unitLabels)}
          notMeasurableText={t("mobile.progress.trends.notMeasurable", {
            name: r.name,
            defaultValue:
              "Not measurable yet: no {{name}} set went near its limit. A set taken close to failure (an AMRAP) would show the strength here.",
          })}
          // ⚠️ Post-PG-24 questo stato NON si attiva più su QUESTO asse: una
          // serie ammessa ha già ≥MIN_TREND_POINTS punti nella stessa finestra
          // che `detectRegression` rifiltra, quindi `reg.pointCount` non può
          // scendere sotto la soglia. Resta obbligatorio perché il ROW è
          // condiviso, e sull'asse reps-at-load (che non ha ammissibilità, solo
          // `comparableDays >= 2`) è vivo. ⛔ Non toglierlo credendolo morto:
          // è morto qui, non lì.
          fewDataText={t("mobile.progress.trends.fewData", {
            days: windowDays,
            defaultValue: "Few data points in {{days}} d",
          })}
          trendLabels={{
            // The same three words the panel's own help legend teaches
            // for ↗ → ↘, so the arrow says out loud what the legend says
            // on screen.
            up: t("mobile.progress.trends.a11yUp", { defaultValue: "progressing" }),
            flat: t("mobile.progress.trends.a11yFlat", { defaultValue: "plateau" }),
            down: t("mobile.progress.trends.a11yDown", { defaultValue: "regressing" }),
          }}
          // EQ-1-HIST slice 2 — only when the athlete actually named a machine.
          // Undefined for every untagged exercise, which is all of production
          // today, so those rows render byte-identically to before.
          machine={
            r.equipmentInstance === undefined
              ? undefined
              : {
                  label:
                    r.alternates.length > 0
                      ? t("mobile.progress.trends.machineMore", {
                          defaultValue: "{{machine}} · +{{count}}",
                          machine: machineLabel(r.equipmentInstance, t),
                          count: r.alternates.length,
                        })
                      : machineLabel(r.equipmentInstance, t),
                  // ⭐ The exercise rides along, the way the logger's machine
                  // chip already does it: a reader landing straight on this
                  // control by navigating between buttons would otherwise hear
                  // "Machine B" without knowing OF WHAT.
                  a11yLabel: t("mobile.progress.trends.machineA11y", {
                    defaultValue:
                      "{{exercise}}, {{machine}}. Show the other {{count}} machines.",
                    exercise: r.name,
                    machine: machineLabel(r.equipmentInstance, t),
                    count: r.alternates.length,
                  }),
                  alternates: r.alternates.map((a) => {
                    // PG-49 — same estimate, same 1-dp rule as the headline above.
                    const value = formatE1rm(a.latest, units, i18n.language, unitLabels);
                    // §3 on the alternate's OWN series (EQ-1-HIST-TRUST): when
                    // none of its points are a trusted floor, the row flags its
                    // value with the same "?" the headline uses.
                    const insufficient = a.trustedCount === 0;
                    return {
                      key: a.equipmentInstance,
                      // The bare machine name, not the qualified one: the
                      // exercise is already the line above these.
                      label: machineLabel(a.equipmentInstance, t),
                      value,
                      detail: t("mobile.progress.trends.machineSessions", {
                        defaultValue: "{{count}} sessions",
                        count: a.pointCount,
                      }),
                      insufficient,
                      // The "?" is decorative and hidden from a11y, so an
                      // untrusted alternate would otherwise read as a bare
                      // number to a screen reader — the meaning glyph-only. Give
                      // the value the same "not measurable" words the headline
                      // gets, so the untrusted state is spoken, not just seen.
                      ...(insufficient
                        ? {
                            valueA11yLabel: t(
                              "mobile.progress.trends.machineNotMeasurable",
                              {
                                defaultValue: "{{value}}, not measurable yet",
                                value,
                              },
                            ),
                          }
                        : {}),
                    };
                  }),
                }
          }
          // ⛔ Only when there is a picker to open — no door, no control.
          {...(ExercisePickerMount
            ? {
                // PG-27 — "Compare" was a word and it said the wrong thing: it
                // read as "add something and compare it to the row". It is now
                // the swap glyph, and it STAYS after a pick because it
                // re-changes, it does not only open. So the spoken label has to
                // work in both states — "choose" alone would go stale the
                // moment a choice exists.
                compareAction: {
                  a11yLabel: t("mobile.progress.trends.compareCtaA11y", {
                    pattern: patternLabel(t, r.pattern),
                    defaultValue:
                      "Change which exercise to compare {{pattern}} with",
                  }),
                  onPress: () => setPickingFor(r.pattern),
                },
                // PG-28 — only when there IS a pick to undo. On a computed
                // pairing there is nothing to go back to: it is already the
                // default, and a ✕ that restores what you are looking at is a
                // control that lies.
                ...(chosen.has(r.pattern)
                  ? {
                      compareClear: {
                        a11yLabel: t("mobile.progress.trends.compareClearA11y", {
                          pattern: patternLabel(t, r.pattern),
                          defaultValue:
                            "Clear your comparison for {{pattern}} and go back to the suggested one",
                        }),
                        onPress: () => clearCompared(r.pattern),
                      },
                    }
                  : {}),
              }
            : {})}
          chart={{
            key: r.slug,
            points: r.sorted.map((p) => ({
              x: p.atMs,
              y: p.kgPerRep1,
              label: dateFmt.format(new Date(p.atMs)),
            })),
            // PG-49: feeds the chart's a11y values and the row's readout; same 1-dp rule.
            yFormat: (n) => formatE1rm(n, units, i18n.language, unitLabels),
            // PG-24 — the exercise this pattern is compared with: his pick
            // when he made one, otherwise the one it moved on from.
            ...(() => {
              const compare = compareFor(r);
              return compare ? { compare } : {};
            })(),
          }}
        />
        ),
      )}
      {ExercisePickerMount ? (
        <ExercisePickerMount
          visible={pickingFor !== null}
          onClose={closePicker}
          onPickSingle={pickCompared}
          // His one rule on the pick: only exercises that HAVE an e1RM.
          eligibleSlugs={comparableSlugs}
        />
      ) : null}
    </View>
  );
}
